import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Shield, ShieldCheck, ShieldOff, Copy, Check, KeyRound, AlertTriangle } from "lucide-react";

export default function MFASettings() {
  const [status, setStatus] = useState(null);
  const [enrolling, setEnrolling] = useState(null); // {provisioning_uri, qr_png_base64}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [showDisable, setShowDisable] = useState(false);

  const loadStatus = () => api.get("/mfa/status").then((r) => setStatus(r.data));
  useEffect(() => { loadStatus(); }, []);

  const startEnroll = async () => {
    try {
      const r = await api.post("/mfa/enroll");
      setEnrolling(r.data);
      setCode("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrollment failed"); }
  };

  const confirmEnroll = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error("6-digit code required");
    try {
      const r = await api.post("/mfa/enroll/confirm", { code });
      setRecovery(r.data.recovery_codes);
      setEnrolling(null);
      setCode("");
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
  };

  if (!status) return <div className="text-sm text-[#94A3B8]">Loading MFA status…</div>;

  return (
    <div className="max-w-2xl" data-testid="mfa-settings">
      <div className="flex items-start gap-3 mb-6">
        <div className={`w-12 h-12 rounded-full grid place-items-center ${status.enabled ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-[#475569]"}`}>
          {status.enabled ? <ShieldCheck className="w-6 h-6" /> : <Shield className="w-6 h-6" />}
        </div>
        <div>
          <h2 className="font-heading text-2xl text-[#0A2540] dark:text-white">Two-factor authentication</h2>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            {status.enabled
              ? "Your account is protected by an authenticator app. Sensitive actions may re-prompt you within 5 minutes of your last verification."
              : "Add an authenticator app (Google Authenticator, 1Password, Authy) as a second factor for high-risk actions like data exports and financial edits."}
          </p>
        </div>
      </div>

      {recovery && (
        <div className="card-pb p-5 mb-6 border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/10" data-testid="recovery-codes">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-heading text-lg text-amber-900 dark:text-amber-200">Save your recovery codes</h3>
          </div>
          <p className="text-xs text-amber-800 dark:text-amber-300 mb-3">
            These 10 one-time codes can bypass MFA if you lose your phone. Store them in a password manager. They will NEVER be shown again.
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm p-3 bg-white dark:bg-slate-900 rounded border">
            {recovery.map((c) => <div key={c} className="text-[#0A2540] dark:text-white">{c}</div>)}
          </div>
          <div className="flex gap-2 mt-3">
            <CopyButton value={recovery.join("\n")} />
            <button onClick={() => setRecovery(null)} className="btn-primary text-xs py-1.5 px-3" data-testid="close-recovery">
              I've saved them
            </button>
          </div>
        </div>
      )}

      {!status.enabled && !enrolling && !recovery && (
        <button onClick={startEnroll} className="btn-accent" data-testid="start-mfa">
          <Shield className="w-4 h-4 mr-2" /> Enable two-factor authentication
        </button>
      )}

      {enrolling && (
        <div className="card-pb p-6" data-testid="mfa-enroll-panel">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3">Step 1: Scan this QR code</h3>
          <div className="flex gap-6 flex-wrap">
            <img
              src={`data:image/png;base64,${enrolling.qr_png_base64}`}
              alt="MFA QR"
              className="w-48 h-48 rounded border"
              data-testid="mfa-qr"
            />
            <div className="flex-1 min-w-[240px]">
              <p className="text-xs text-[#475569] dark:text-slate-400 mb-2">Or paste this into your app manually:</p>
              <code className="text-[11px] break-all block p-2 bg-slate-50 dark:bg-slate-800 rounded border">{enrolling.provisioning_uri}</code>
              <p className="text-xs text-[#475569] dark:text-slate-400 mt-3 mb-1">Step 2: Enter the 6-digit code from your app</p>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                data-testid="mfa-code"
                autoFocus
                inputMode="numeric"
                placeholder="123456"
                className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-lg tracking-widest font-mono text-center"
              />
              <div className="flex gap-2 mt-3">
                <button onClick={confirmEnroll} disabled={code.length !== 6} className="btn-accent text-sm flex-1" data-testid="confirm-mfa">Verify & enable</button>
                <button onClick={() => { setEnrolling(null); setCode(""); }} className="btn-outline-navy text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {status.enabled && !enrolling && (
        <div className="card-pb p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-[#0A2540] dark:text-white inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> MFA is active
              </div>
              <div className="text-xs text-[#475569] dark:text-slate-400 mt-1">
                Verified {status.elevated_seconds_remaining > 0 ? `— re-verification required in ${Math.floor(status.elevated_seconds_remaining / 60)}m ${status.elevated_seconds_remaining % 60}s` : "— re-verification required for sensitive actions"}
              </div>
            </div>
            <button onClick={() => setShowDisable(true)} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid="disable-mfa-btn">
              <ShieldOff className="w-3.5 h-3.5" /> Disable MFA
            </button>
          </div>
        </div>
      )}

      {showDisable && (
        <DisableModal onClose={() => setShowDisable(false)} onDone={() => { setShowDisable(false); loadStatus(); }} />
      )}
    </div>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="btn-outline-navy text-xs py-1.5 px-3 inline-flex items-center gap-1" data-testid="copy-recovery">
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy all"}
    </button>
  );
}

function DisableModal({ onClose, onDone }) {
  const [mode, setMode] = useState("code"); // 'code' | 'recovery'
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = mode === "code" ? { code: value } : { recovery_code: value };
      await api.post("/mfa/disable", payload);
      toast.success("MFA disabled");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="disable-modal">
        <div className="flex items-center gap-2 mb-3">
          <ShieldOff className="w-5 h-5 text-red-600" />
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">Disable MFA</h3>
        </div>
        <p className="text-xs text-[#475569] dark:text-slate-400 mb-4">
          Provide a valid 6-digit code from your app or one of your recovery codes to confirm.
        </p>
        <div className="flex gap-2 mb-3">
          <button onClick={() => { setMode("code"); setValue(""); }}
                  className={`text-xs px-3 py-1 rounded-full ${mode === "code" ? "bg-[#0A2540] text-white" : "bg-slate-100 text-[#475569]"}`}>Authenticator</button>
          <button onClick={() => { setMode("recovery"); setValue(""); }}
                  className={`text-xs px-3 py-1 rounded-full ${mode === "recovery" ? "bg-[#0A2540] text-white" : "bg-slate-100 text-[#475569]"}`}>
            <KeyRound className="w-3 h-3 inline mr-1" /> Recovery code
          </button>
        </div>
        <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="disable-value"
               placeholder={mode === "code" ? "123456" : "xxxx-xxxx-xxxx"}
               className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm font-mono" />
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={submit} disabled={saving || !value} className="btn-primary text-sm bg-red-600 hover:bg-red-700" data-testid="confirm-disable">
            {saving ? "Disabling…" : "Disable MFA"}
          </button>
        </div>
      </div>
    </div>
  );
}
